import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { format } from "date-fns";
import { SubSink } from "subsink";
import { PpsUtilityService } from "../services/pps-utility.service";
import { RosterService } from "../services/roster.service";
import { IInvalidEmployeeRoster } from "../shared/models/roster/invalid-employee-roster";
import { IPayrollRosterCodeUpdateModel } from "../shared/models/roster/payroll-roster-code-update-mode";
import { IRosterCode } from "../shared/models/roster-code";
import { ToastService } from "../shared/toast/toast.service";

interface InvalidEmployeeRosterViewModel{
  employeeId: number;
  day: Date,
  employeeName: string;
  dayStr: string;
  rosterCode: string;
  currentPayrollRosterCode: IRosterCode;
  suggestedPayrollRosterCode: IRosterCode;

  canUpdatePayrollRosterCode: boolean;
  payrollRosterCodeUpdateModel: IPayrollRosterCodeUpdateModel
}

@Component({
  selector: "pps-invalid-employee-roster",
  templateUrl: "./invalid-employee-rosters.component.html"
})
export class InvalidEmployeeRostersComponent implements OnChanges{
  @Input() from: Date;
  @Input() to: Date;
  @Input() rosterProjectId: number;
  @Output() onSelect = new EventEmitter<{rowId: string, colId: string, autoOpenForm?: boolean}>();
  @Output() onPayrollRosterCodeUpdate = new EventEmitter<{rowId: string, colId: string, payrollRosterCode: IRosterCode}>();
  @Output() onReloadRoster = new EventEmitter();

  private subs = new SubSink();
  
  message = "";
  messageSeverity = "success";
  validating = false;
  shouldReloadRoster: boolean = false;
  totalNumberFixed: number = 0;
  totalInvalidNumber: number = 0;

  InvalidEmployeeRosters: InvalidEmployeeRosterViewModel[] = [];
  selectedRoster: InvalidEmployeeRosterViewModel = null;
  autoOpenEmployeeRosterForm = false;

  constructor(
    private _rosterService: RosterService,
    private _toast: ToastService,
    private _util: PpsUtilityService
  ){
  }

  ngOnChanges(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }  

  loadData(){
    this.InvalidEmployeeRosters = [];
    this.selectedRoster = null;
    this.validating = true;
    this.subs.sink = this._rosterService.getInvalidEmployeeRosters(this.rosterProjectId, this.from, this.to)
      .subscribe(res => {
        if (res.succeed){
          this.InvalidEmployeeRosters = res.result.map(ier=>{
            return {
              employeeId: ier.employee.id,
              employeeName: this._util.getEmployeeName(ier.employee),
              day: ier.day,
              dayStr: format(ier.day, "dd/MM/yyyy"),
              rosterCode: ier.rosterCode.code,
              currentPayrollRosterCode: ier.currentPayrollRosterCode,
              suggestedPayrollRosterCode: ier.suggestedPayrollRosterCode,

              canUpdatePayrollRosterCode: ier.suggestedPayrollRosterCode?.id > 0,
              payrollRosterCodeUpdateModel: {
                employeeId: ier.employee.id,
                day: ier.day,
                oldValue: ier.currentPayrollRosterCode?.id,
                newValue: ier.suggestedPayrollRosterCode?.id
              }
            }
          });

          this.totalInvalidNumber = this.InvalidEmployeeRosters.length;
          if (this.totalInvalidNumber === 0){
            this.message = `
              All payroll roster codes between ${format(this.from, "dd/MM/yyyy")} and ${format(this.to, "dd/MM/yyyy")} are good.              
              `;
            this.messageSeverity = "success";
          }
        }else{
          this.message = res.errors.join(";");
          this.messageSeverity = "error";
        }
      },
      err => {},
      () => {
        this.validating = false;
      });
  }

  onRowSelect(row){
    let colId = format(this.selectedRoster.day, "yyyyMMdd");
    let rowId = this.selectedRoster.employeeId;
    this.onSelect.emit({rowId: rowId + "", colId: colId, autoOpenForm: this.autoOpenEmployeeRosterForm});
  }

  fixPayrollRosterCode(invalidEmployeeRoster: InvalidEmployeeRosterViewModel){
    this.subs.sink = this._rosterService.updatePayrolLRosterCode(this.rosterProjectId, [invalidEmployeeRoster.payrollRosterCodeUpdateModel])
      .subscribe(res=>{
        if (res.succeed){
          let colId = format(invalidEmployeeRoster.day, "yyyyMMdd");
          let rowId = invalidEmployeeRoster.employeeId;
          this.onPayrollRosterCodeUpdate.emit({rowId: rowId + "", colId: colId, payrollRosterCode: invalidEmployeeRoster.suggestedPayrollRosterCode});
          this.shouldReloadRoster = true;
          this.totalNumberFixed ++;

          // remove the fixed roster code from invalid roster code list
          let index = this.InvalidEmployeeRosters.findIndex(r => r.employeeId === invalidEmployeeRoster.employeeId && r.day === invalidEmployeeRoster.day);
          if (index !== -1){
            this.InvalidEmployeeRosters.splice(index, 1);
          }

          this._toast.succeeded("Successfully updated the payroll roster code");
        }else{
          this._toast.failed("Failed to update the payroll roster code");
        }      
      });
  }

  reloadRoster(){
    this.onReloadRoster.emit();
  }
}